import requestWrapper, { ICallerOptions } from "./request";
import axios from "axios";
import { IApplicationContact, IApplicationProject, Guid, IVersionedCall, IApplicationDetails, IApplicationBudget, IApplicationTargetGroup, IAttachmentReference, AttachmentType, IApplicationContactUpdate } from './types';
import { withCacheItem } from "./request/cache";

const basePath = '/api/application'
export function getApplicationContact(applicationId: Guid, index: 1 | 2, options?: ICallerOptions): Promise<IApplicationContact> {
    return requestWrapper<IApplicationContact | string>(() => axios.get(`${basePath}/${applicationId}/contact/${index}`), options)
        .then(response => {
            if (typeof (response) === 'string')
                return null;
            else if (response.contactId === '00000000-0000-0000-0000-000000000000')
                return {
                    ...response,
                    contactId: null
                };
            else
                return response;
        });
}

export function submitApplicationContact(applicationId: Guid, index: 1 | 2, contact: IApplicationContactUpdate, options?: ICallerOptions): Promise<void> {
    if (index !== 2)
        throw new Error('Not Allowed');

    return requestWrapper(() => axios.patch(`${basePath}/${applicationId}/contact`, contact), options);
}


export function getApplicationProject(applicationId: Guid, options?: ICallerOptions): Promise<IApplicationProject> {
    return requestWrapper(() => axios.get(`${basePath}/${applicationId}/general`), options);
}
export function submitApplicationProject(applicationId: Guid, project: Partial<IApplicationProject>, options?: ICallerOptions): Promise<void> {
    return requestWrapper(() => axios.patch(`${basePath}/${applicationId}/general`, project), options);
}

export function getApplicationBudget(applicationId: Guid, options?: ICallerOptions): Promise<IApplicationBudget> {
    return requestWrapper(() => axios.get(`${basePath}/${applicationId}/finance`), options);

}
export function submitApplicationBudget(applicationId: Guid, budget: Partial<IApplicationBudget>, options?: ICallerOptions): Promise<void> {
    return requestWrapper(() => axios.patch(`${basePath}/${applicationId}/finance`, budget), options);
}

export function getApplicationTargetGroup(applicationId: Guid, options?: ICallerOptions): Promise<IApplicationTargetGroup> {
    return requestWrapper(() => axios.get(`${basePath}/${applicationId}/targetgroup`), options);
}
export function submitApplicationTargetGroup(applicationId: Guid, targetGroup: Partial<IApplicationTargetGroup>, options?: ICallerOptions): Promise<void> {
    return requestWrapper(() => axios.patch(`${basePath}/${applicationId}/targetgroup`, targetGroup), options);
}
export function getApplicationSpecific(applicationId: Guid, options?: ICallerOptions): Promise<{ applicationID: Guid, values: { [key: string]: any } }> {
    return requestWrapper(() => axios.get(`${basePath}/${applicationId}/specific`), options);
}
export function submitApplicationSpecific(applicationId: Guid, values: { [key: string]: any }, options?: ICallerOptions): Promise<void> {
    return requestWrapper(() => axios.patch(`${basePath}/${applicationId}/specific`, values), options);
}

export function getApplicationAttachments(applicationId: Guid, options?: ICallerOptions): Promise<IAttachmentReference[]> {
    return requestWrapper(() => axios.get(`${basePath}/${applicationId}/attachment`), options);
}
export function getApplicationAttachmentsByType(applicationId: Guid, type: AttachmentType, options?: ICallerOptions): Promise<IAttachmentReference[]> {
    return getApplicationAttachments(applicationId, options)
        .then((result) => result.filter((x) => x.type === type));
}
export function deleteApplicationAttachment(applicationId: Guid, type: string, filename: string, options?: ICallerOptions): Promise<void> {
    return requestWrapper(() => axios.delete(`${basePath}/${applicationId}/attachment/${type}/${filename}`), options);
}
export function submitApplicationAttachment(applicationId: Guid, type: string, filename: string, contents: Blob, options?: ICallerOptions): Promise<IAttachmentReference> {
    const formData = new FormData();
    formData.append("file", contents, filename);

    return requestWrapper(
        () => axios.post(
            `${basePath}/${applicationId}/attachment/${type}`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        ),
        { ...options, allowRetry: options && typeof (options.allowRetry) === 'boolean' ? options.allowRetry : false }
    );
}

export const getCallForApplication = withCacheItem(
    (applicationId: string) => applicationId,
    (applicationId: string, options?: ICallerOptions): Promise<IVersionedCall> => {
        return requestWrapper(() => axios.get(`${basePath}/${applicationId}/call`), options);
    });

export function getApplication(applicationId: string, options?: ICallerOptions): Promise<IApplicationDetails> {
    return requestWrapper(() => axios.get(`${basePath}/${applicationId}`), options);
}

interface ISendResult {
    result: 'Success' | 'InvalidData' | 'AlreadyCompleted';
    errors: Array<{
        field: string,
        reason: 'Missing' | 'OutOfRange'
    }>;
};

export function applicationSend(applicationId: Guid, options?: ICallerOptions): Promise<ISendResult> {
    return requestWrapper(() => axios.post(`${basePath}/${applicationId}/submit`), options);
}